import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GTM_EVENTS, SUBSCRIPTION_CONSTANTS } from '@constants/app.constants';
import { GA4_EVENT_NAMES } from '@constants/ga4-event-names.constants';
import { WINDOW_LISTENER_CONSTANTS } from '@constants/window-listener.constants';
import { environment } from '@environments/environment';
import { CUSTOM_CSS_WIDGET_TYPE } from '@models/css-overwrites.model';
import { StatisticsTraceModel } from '@models/appointment-booking.model';
import { SubscriptionDao } from '@models/partner-db.model';
import { TranslateService } from '@ngx-translate/core';
import { BookingService } from '@services/feature/booking.service';
import { WidgetService } from '@services/feature/widget.service';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

declare var dataLayer: any;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {

  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  protected bookingService = inject(BookingService);
  private widgetService = inject(WidgetService);
  private helperService = inject(HelperService);
  private translateService = inject(TranslateService);

  private sessionUuid: string;
  private _partnerSubscriptionPlan: SubscriptionDao;

  set partnerSubscriptionPlan(subscriptionPlan: SubscriptionDao) {
    this._partnerSubscriptionPlan = subscriptionPlan;
  }

  async emitAppointmentBookingEvent(eventName: string): Promise<void> {
    const type = 'TRACKER_TRIGGER_VIEW_END';

    // Get current language
    const lang = this.translateService.getDefaultLang();

    // Get current appointment selection from booking service
    const currentAppointment = { ...this.bookingService.selectedBookingData.currentSelectedAppointmentState };

    // Prepare a filtered version of selected services with only required fields
    const filteredServiceData = currentAppointment.selectedServices.map(
      ({ name, appointment_service_category, description, duration, id, _translations }) => ({
        name,
        appointment_service_category,
        description,
        duration,
        id,
        _translations,
      })
    );

    const filteredCartItems = this.bookingService.selectedBookingData?.cartItems.map(item => ({
      date: item.date,
      endTime: item.endTime,
      isAppointmentReserved: item.isAppointmentReserved,
      selectedMeetingTypeId: item.selectedMeetingTypeId,
      service: item.service,
      serviceId: item.serviceId,
      startHour: item.startHour,
      startTime: item.startTime,
      worker: item.worker,
      workerId: item.workerId,
      workerUuids: item.workerUuids
    }));

    const rawParams = {
      meeting_type_id: currentAppointment.selectedMeetingTypeId,
      meeting_type: this.bookingService?.meetingTypeList?.find(meeting => meeting.id === currentAppointment.selectedMeetingTypeId),
      service_ids: currentAppointment.selectedServiceIds,
      selected_store: currentAppointment.selectedStore,
      selected_store_uuids: currentAppointment.selectedStoreUuids,
      selected_worker: currentAppointment.selectedWorker,
      appointment_service: filteredServiceData,
      user_selected_language: lang,
      appointments: filteredCartItems
    };

    // Clean up the params object by removing empty values
    const params = this.helperService.removeEmptyValues(rawParams);

    // Google Tag Manager
    try {
      dataLayer.push({
        event: this.toGa4EventName(eventName),
        widget_type: CUSTOM_CSS_WIDGET_TYPE.smart_widget,
        ...params
      });
    } catch (error) {
      LoggerService.warn('Datalayer not defined error is handled');
    }

    // Adobe launch tracking
    try {
      const someClick = new CustomEvent(type, {
        detail: {
          name: eventName,
          params
        }
      });
      window.dispatchEvent(someClick);
    } catch (error) {
      LoggerService.warn('Adobe event error handler');
    }

    // Only enterprise partners can access this feature (legacy + v2)
    if ((this._partnerSubscriptionPlan?.subscription_type_id === SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_NEW_ENTERPRISE_TYPE.id ||
      this._partnerSubscriptionPlan?.subscription_type_id === SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_ENTERPRISE_TYPE.id ||
      this._partnerSubscriptionPlan?.subscription_type_id === SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_ENTERPRISE_TYPE.id)) {
      !this.sessionUuid && this.generateSessionUuid();
      this.emitBookingDetails(eventName, params);
    }

    // Send appointment booking steps to parent website
    this.sendAppointmentBookingStepsToParentWebsite(eventName, params);
  }

  sendAppointmentBookingStepsToParentWebsite(eventName: string, params: any): void {
    if (this.widgetService?.parentToChildOriginEvent) {
      this.widgetService.sendMessageToMainJs(
        this.widgetService.parentToChildOriginEvent,
        {
          event: WINDOW_LISTENER_CONSTANTS.CALENSO_CHILD_TO_PARENT_EVENT,
          note: 'Child To Parent Communication Done',
          step: eventName,
          ...params && { params: params }
        }
      );
    }
  }

  emitSurveyEvent(data: { eventName: string, eventStep: number, stepName: string }): void {
    // Google Tag Manager
    try {
      dataLayer.push({
        event: this.toGa4EventName(data.eventName),
        widget_type: CUSTOM_CSS_WIDGET_TYPE.smart_widget,
        step: data.eventStep,
        stepName: data.stepName
      });
    } catch (error) {
      LoggerService.warn('Datalayer not defined error is handled');
    }

    // Adobe launch tracking
    try {
      const type = 'TRACKER_TRIGGER_VIEW_END';
      const someClick = new CustomEvent(type, {
        detail: {
          name: data.eventName
        }
      });
      window.dispatchEvent(someClick);
    } catch (error) {
      LoggerService.warn('Adobe event error handler');
    }

    // Only enterprise partners can access this feature (legacy + v2)
    if ((this._partnerSubscriptionPlan?.subscription_type_id === SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_NEW_ENTERPRISE_TYPE.id ||
      this._partnerSubscriptionPlan?.subscription_type_id === SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_ENTERPRISE_TYPE.id ||
      this._partnerSubscriptionPlan?.subscription_type_id === SUBSCRIPTION_CONSTANTS.SUBSCRIPTION_V2_ENTERPRISE_TYPE.id)) {
      !this.sessionUuid && this.generateSessionUuid();
      this.emitBookingDetails(data.eventName);
    }
  }

  private emitBookingDetails(eventName: string, metaData: any = null): void {
    const param = {
      session_id: this.sessionUuid,
      widget_type: 'smart_widget',
      event: eventName,
      appointment_type: this.extractBookingType(eventName),
      // Attribute the event to the specific smart-widget so the dashboard Conversion
      // Tracking funnel can be filtered per widget. This rides on the internal
      // `statistics/trace` payload (updateBookingDetails), NOT the Google Analytics /
      // GTM dataLayer push. Guarded so we never send `undefined`.
      ...this.widgetService.widgetUUID && { widget_uuid: this.widgetService.widgetUUID },
      ...metaData && { event_meta: JSON.stringify(metaData) }
    };
    this.updateBookingDetails(param)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: error => LoggerService.log(error)
      });
  }

  private toGa4EventName(eventName: string): string {
    const key = (Object.keys(GTM_EVENTS) as Array<keyof typeof GTM_EVENTS>).find(k => GTM_EVENTS[k] === eventName);
    return key ? GA4_EVENT_NAMES[key] : eventName;
  }

  private extractBookingType(eventName: string): string | null {
    if (eventName.startsWith('appointment_')) {
      return 'appointment';
    } else if (eventName.startsWith('survey_')) {
      return 'survey';
    }
    return null;
  }

  private updateBookingDetails(params: Partial<StatisticsTraceModel>): Observable<{ success: boolean, message: string }> {
    return this.http.post<{ success: boolean, message: string }>(environment.restAPI + 'statistics/trace', params, {});
  }

  generateSessionUuid(): void {
    this.sessionUuid = uuidv4();
  }

  triggerApoointmentStartEvent(): void {
    if (this.bookingService.gtmStartTriggered === false) {
      this.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_start);
      this.bookingService.gtmStartTriggered = true;
    }
  }
}